









































































































































import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
import {Vue, Prop, Component} from 'vue-property-decorator';
import VueElementLoading from "vue-element-loading";
import popUp from "@/modules/budget/monitoring/input-forms/components/popUp.vue";
import ReasonInputApp19 from "@/modules/budget/monitoring/input-forms/components/application19/ReasonInputApp19.vue";
import { Entry, Reason, formatDecimal, parseFormattedNumber } from './types/Entry';
import { Ax } from '@/utils';
import i18nService from "@/services/i18n"
import { cloneDeep } from 'lodash';

const trPath = 'modules.budget_execution_monitoring.reports.app19'

@Component({
    components: {
        'reason-input': ReasonInputApp19,
        'loading': VueElementLoading,
        'pop-up': popUp,
    }
})
export default class Application19 extends Vue {
    @Prop({
        required: true
    })
    private userId!: string

    @Prop({
        required: true
    })
    private currForm!: any

    private year: number = this.currForm.year

    private entries: Entry[] = []
    private initialEntries: Entry[] = []
    
    get reasonEligibleEntries(): Entry[] {
        return this.initialEntries.filter(
            it => it.undevelopedSum !== 0 || it.reasons.length > 0
        )
    }

    public onNumberInput(value: string | null, entry: Entry, field: keyof Entry) {
        const parsed = value == null ? null : parseFormattedNumber(value)
        // @ts-ignore
        entry[field] = parsed
    }

    public tr(key: string): string {
        const text = String(this.$t(`${trPath}.${key}`))
        return text
    }

    public get tableFields(): (keyof Entry)[] {
        const fields: (keyof Entry)[] = [
            'republicKbk',
            'localKbk',
        ] 

        if (this.isSummaryForm) {
            fields.push('region')
        }

        fields.push('project')
        fields.push('allowedSum')
        fields.push('acceptedObligations')
        fields.push('paidObligaitons')
        fields.push('undevelopedSum')
        fields.push('returnedSum')
        fields.push('reasons')

        return fields
    }

    public loading: boolean = false
    public showReasonInput: boolean = false

    public isAgreedForm = false

    get isInProgressForm() {
        return !this.isAgreedForm
    }

    get isSummaryForm() {
        return ['app19', 'app13cons', 'app17'].includes(this.currForm.form_code)
    }

    get isNonSummaryForm() {
        return !this.isSummaryForm
    }

    public formatDecimal = formatDecimal

    private isStatusAgreed(status: number) {
        return ![73, 27].includes(status)
    }

    private async created() {
        await this.loadEntries()

        this.isAgreedForm = this.isStatusAgreed(this.currForm.status.status)

        this.$emit('onRegisterFacade', this)
    }

    public setReturnedSumZeroIfDisabled(entry: Entry): boolean {
        const isDisabled = entry.undevelopedSum === 0
        if (isDisabled) entry.returnedSum = 0
        return isDisabled
    }

    public hasSubData(entry: Entry): boolean {
        return Boolean(entry.acceptedObligations)
            || Boolean(entry.paidObligaitons)
            || Boolean(entry.returnedSum)
            || entry.reasons.length > 0
    }

    public calculateTotal(field: keyof Entry) {
        let sum = 0
        this.entries.forEach(it => {
            if (typeof it[field] === 'number') {
                // @ts-ignore
                sum += Math.round(it[field] * 100)
            } else if (typeof it[field] === 'string') {
                // @ts-ignore
                sum += Math.round(parseFormattedNumber(it[field]) * 100)
            }
        })
        return sum / 100
    }

    private async fetchEntries(
        formCode: string,
        year: number,
        region: string,
        abp: string | null,
    ): Promise<Entry[]> {
        let entries: Entry[] = []

        try {
            this.loading = true

            const params = new URLSearchParams()
            params.set('form_code', formCode)
            params.set('year', year.toString())
            params.set('region', region)
            if (abp) params.set('abp', abp)
            const response = await fetch(`/api-py/application/input-forms/19/data?${params}`)
            if (!response.ok) {
                const errorMessage = await response.json().catch(() => null)
                throw new Error(errorMessage || `Request failed with status ${response.status}`)
            }
            const responseItems: any[] = await response.json()

            entries = responseItems.map((it, index) => {
                const reasons: Reason[] = it.reasons.map((it: any) => {
                    return new Reason(
                        it.id,
                        it.sum,
                        it.reason_ru,
                        it.reason_kk,
                    )
                })

                return new Entry(
                    index,
                    it.application19_id ?? null,
                    it.func_rb,
                    it.func,
                    it.region,
                    it.region_name_ru || '',
                    it.region_name_kk || '',
                    it.expense_code,
                    it.expense_code_name_ru || '',
                    it.expense_code_name_kk || '',
                    it.reused_amount || null,
                    it.registrob,
                    it.sumrg,
                    it.refunded_amount,
                    reasons,
                )
            })
        } catch (error) {
            console.error('Failed to load form data:', error)
            this.makeToast('danger', this.tr('error'), error)
        } finally {
            this.loading = false
        }

        return entries
    }

    private async loadEntries() {
        const entries = await this.fetchEntries(
            this.currForm.form_code,
            this.year,
            this.currForm.region,
            this.currForm.abp || null
        )
        this.entries = entries
        this.initialEntries = cloneDeep(entries)
    }

    public async saveData() {
        if (this.isAgreedForm) {
            this.makeToast(
                'info',
                this.tr('message'),
                this.tr('blocked_edit.in_progress_form'),
            )
            return
        }
        // Check parent's status
        if (this.currForm.parent && this.isStatusAgreed(this.currForm.parent.status.status)) {
            this.makeToast(
                'info',
                this.tr('message'),
                this.tr('blocked_edit.parent_form_in_progress'),
            )
            return
        }

        if (this.currForm.form_code === 'app19') {
            await this.edit19Data()
        } else {
            await this.editData()
        }
    }

    public async edit19Data() {
        type SaveEntryRequest = { // TODO: move to outer scope
            func_rb: string
            func: string
            region: string
            expense_code: string
            reused_amount: number
        }
        type EditEntryRequest = { // TODO: move to outer scope
            id: number
            reused_amount: number
        }
        type EditFormRequest = {
            budget_execution_forms_id: number
            user_id: string
            save_entries: SaveEntryRequest[]
            edit_entries: EditEntryRequest[]
            delete_entries_ids: number[]
        }

        try {
            const deleteIds: number[] = this.entries
                .filter(it =>
                    it.id
                    && !it.allowedSum
                    && !it.acceptedObligations
                    && !it.paidObligaitons
                    && !it.returnedSum
                )
                .map(it => it.id!!)
            
            const editEntries: EditEntryRequest[] = this.entries
                .filter(it =>
                    it.id
                    && !this.hasSubData(it)
                )
                .map(it => {
                    const mapped: EditEntryRequest = {
                        id: it.id!!,
                        reused_amount: it.allowedSum ?? 0,
                    }
                    return mapped
                })

            const saveEntries: SaveEntryRequest[] = this.entries
                .filter(it =>
                    !it.id
                    && it.allowedSum
                )
                .map(it => {
                    const mapped: SaveEntryRequest = {
                        func_rb: it.republicKbk,
                        func: it.localKbk,
                        region: it.regionCode,
                        expense_code: it.projectCode,
                        reused_amount: it.allowedSum ?? 0,
                    }
                    return mapped
                })

            const requestBody: EditFormRequest = {
                budget_execution_forms_id: this.currForm.id,
                user_id: this.userId,
                save_entries: saveEntries,
                edit_entries: editEntries,
                delete_entries_ids: deleteIds,
            }

            const response = await fetch('/api-py/application/input-forms/19/data', {
                method: 'PUT',
                body: JSON.stringify(requestBody),
            })

            if (!response.ok) {
                const error: any = await response.json().catch(() => null)

                if (error?.detail?.duplicate_projects) {
                    const projectCodes: string[] = Array.isArray(error?.detail?.duplicate_projects) ? error?.detail?.duplicate_projects : []
                    this.makeToast(
                        'warning',
                        this.tr('attention'),
                        this.tr('_error.duplicate_projects').replace('$projectCodes', projectCodes.join(', ')),
                    )
                    return
                }
                if (error?.detail?.projects) {
                    const projectCodes: string[] = Array.isArray(error?.detail?.projects) ? error?.detail?.projects : []
                    this.makeToast(
                        'danger',
                        this.tr('error'),
                        this.tr('_error.has_subdata').replace('$projectCodes', projectCodes.join(', ')),
                    )
                }

                throw new Error(error || `Request failed with status ${response.status}`)
            }

            this.makeToast('success', this.$t('modules.common.saving'), this.$t('modules.common.data_saved_successfully'))

            await this.loadEntries()
        } catch (error) {
            console.error('Failed to load form data:', error)
            this.makeToast('danger', this.tr('error'), error)
        }
    }

    public async editData() {
        type EditEntryRequest = { // TODO: move to outer scope
            id: number
            registrob: number
            sumrg: number
            refunded_amount: number
        }
        type EditFormRequest = {
            user_id: string
            form_id: number
            entries: EditEntryRequest[]
        }
        
        try {
            const entryRequests: EditEntryRequest[] = this.entries
                .map(it => {
                    const temp: EditEntryRequest = {
                        id: it.id!!,
                        registrob: it.acceptedObligations ?? 0,
                        sumrg: it.paidObligaitons ?? 0,
                        refunded_amount: it.returnedSum ?? 0,
                    }
                    
                    return temp
                })

            const requestBody: EditFormRequest = {
                user_id: this.userId,
                form_id: this.currForm.id,
                entries: entryRequests,
            }

            const response = await fetch('/api-py/application/input-forms/19/sub-data', {
                method: 'PUT',
                body: JSON.stringify(requestBody),
            })

            if (!response.ok) {
                const errorMessage = await response.json().catch(() => null)
                throw new Error(errorMessage || `Request failed with status ${response.status}`)
            }

            const responseBody = await response.json()
            
            responseBody.violations.forEach((it: any) => {
                this.makeToast('warning', this.tr('message'), it[`name_${i18nService.locale}`])
            })

            this.makeToast('success', this.$t('modules.common.saving'), this.$t('modules.common.data_saved_successfully'))

            await this.loadEntries()
        } catch (error) {
            console.error('Failed to load form data:', error)
            this.makeToast('danger', this.tr('error'), error)
        }
    }

    public async onBack() {
        return true
    }

    public async onReasonBack() {
        // Assign newly loaded reasons to existing entries
        const newReasonEntries = await this.fetchEntries(
            this.currForm.form_code,
            this.year,
            this.currForm.region,
            this.currForm.abp || null
        )

        const reasonsMap: Map<number, Reason[]> = new Map()
        newReasonEntries.forEach(entry => {
            if (entry.id && entry.reasons.length > 0) {
                reasonsMap.set(entry.id, entry.reasons)
            }
        })

        this.entries = this.entries.map(entry => {
            entry.reasons = reasonsMap.get(entry.id!!) ?? []
            return entry
        })
        this.initialEntries = cloneDeep(this.entries)

        this.showReasonInput = false
        this.$emit('onCloseReasons')
    }

    public downloadReport(formCode: string) {
        this.loading = true
        const that = this
        
        try {
            this.makeToast('info', this.$t('modules.common.attention'), this.$t('modules.budget_execution_monitoring.reports.app_9.warn_download'))
            
            const params = new URLSearchParams()
            params.set('form_code', formCode)
            params.set('year', this.year.toString())
            params.set('region', this.currForm.region)
            params.set('locale', i18nService.locale)
            if (this.currForm.abp) params.set('abp', this.currForm.abp)

            Ax(
                {
                    url: `/api-py/application/input-forms/19/export?${params}`,
                    method: 'get',
                    responseType: 'blob',
                },
                (data: any) => {
                    const url = window.URL.createObjectURL(new Blob([data]))
                    const link = document.createElement('a')
                    link.href = url
                    const formName = this.tr(`forms.${formCode}`)
                    link.setAttribute('download', formName + '.xlsx')
                    document.body.appendChild(link)
                    link.click()
                    that.loading = false
                },
                (error) => {
                    that.loading = false
                    this.makeToast('danger', this.tr('download_error'), error.toString())
                }
            )
        } catch (error) {
            that.loading = false
            this.makeToast('danger', this.$t('modules.common.warning'), this.$t('modules.budget_execution_monitoring.reports.app_9.warn_filter'))
        }
    }

    public downloadApp9Report() {
        this.loading = true
        const that = this
        
        try {
            this.makeToast('info', this.$t('modules.common.attention'), this.$t('modules.budget_execution_monitoring.reports.app_9.warn_download'))
            
            const params = new URLSearchParams()
            params.set('year', this.year.toString())
            params.set('region', this.currForm.region)
            params.set('locale', i18nService.locale)

            Ax(
                {
                    url: `/api-py/application/input-forms/9/export?${params}`,
                    method: 'get',
                    responseType: 'blob',
                },
                (data: any) => {
                    const url = window.URL.createObjectURL(new Blob([data]))
                    const link = document.createElement('a')
                    link.href = url
                    const formName = String(
                        this.$t('modules.budget_execution_monitoring.reports.app9._filenames.app9')
                    ).replace('$year', this.year.toString())
                    link.setAttribute('download', formName + '.xlsx')
                    document.body.appendChild(link)
                    link.click()
                    that.loading = false
                },
                (error) => {
                    that.loading = false
                    this.makeToast('danger', this.tr('download_error'), error.toString())
                }
            )
        } catch (error) {
            that.loading = false
            this.makeToast('danger', this.$t('modules.common.warning'), this.$t('modules.budget_execution_monitoring.reports.app_9.warn_filter'))
        }
    }

    public get downloadList() {
        const reportList: any[] = [
            {
                fn: this.downloadReport,
                args: [this.currForm.form_code],
                name: this.tr(`forms.${this.currForm.form_code}`),
                idx: 0
            }
        ]

        if (this.currForm.form_code === 'app19') {
            reportList.push({
                fn: this.downloadReport,
                args: ['app8'],
                name: this.tr('forms.app8'),
                idx: 1
            })
            reportList.push({
                fn: this.downloadApp9Report,
                args: [],
                name: this.$t('modules.budget_execution_monitoring.reports.app9.forms.app9'),
                idx: 2
            })
        }

        return reportList
    }

    private makeToast(variant: 'success' | 'danger' | 'warning' | 'info', title: any, tostbody: any) {
        this.$bvToast.toast(tostbody, {
            title: title,
            variant: variant,
            toaster: 'b-toaster-top-center',
            autoHideDelay: 5000,
            appendToast: true
        });
    }
}
